// useXP — current user's points, level, tier. Subscribes to changes via realtime.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UserXP {
  user_id: string;
  total_xp: number;
  weekly_xp: number;
  level: number;
  tier: string;
  weekly_reset_at: string;
}

const EMPTY: UserXP = {
  user_id: "",
  total_xp: 0,
  weekly_xp: 0,
  level: 1,
  tier: "script_kiddie",
  weekly_reset_at: new Date().toISOString(),
};

export const useXP = () => {
  const { user } = useAuth();
  const [xp, setXp] = useState<UserXP | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!user) {
      setXp(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("user_xp")
      .select("user_id, total_xp, weekly_xp, level, tier, weekly_reset_at")
      .eq("user_id", user.id)
      .maybeSingle();
    setXp(data ?? { ...EMPTY, user_id: user.id });
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    if (!user) return;
    // Unique channel name per hook instance — multiple components can mount
    // useXP simultaneously, and Supabase will throw if we try to add `.on()`
    // listeners to a reused (already-subscribed) channel.
    const channelName = `user_xp:${user.id}:${Math.random().toString(36).slice(2, 10)}`;
    const ch = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_xp", filter: `user_id=eq.${user.id}` },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return { xp, loading, refresh };
};

// Helpers shared across points-aware components.
//
// Tier labels are intentionally plain-English so beginners feel oriented at
// a glance ("am I a beginner or a pro?"). The underlying DB enum keys
// (script_kiddie, apprentice, ...) are unchanged — only the display label
// is friendly. We also export a tier *order* so we can compute "next tier"
// in progress bars without hardcoding it in every component.
export const TIER_ORDER = [
  "script_kiddie",
  "apprentice",
  "operator",
  "hacker",
  "wizard",
  "architect",
] as const;

// Mastery arc — six rungs on the path to AI Mastery. Underlying DB enum
// keys (script_kiddie, apprentice, ...) are unchanged; only the display
// label is reframed so the journey reads as Novice → Master.
const TIER_LABELS: Record<string, string> = {
  script_kiddie: "Novice",
  apprentice: "Apprentice",
  operator: "Operator",
  hacker: "Hacker",
  wizard: "Architect",
  architect: "Master",
};

export const tierLabel = (tier: string): string => TIER_LABELS[tier] ?? tier;

// Returns the next tier label after `tier`, or null if already at the top.
export const nextTierLabel = (tier: string): string | null => {
  const idx = TIER_ORDER.indexOf(tier as (typeof TIER_ORDER)[number]);
  if (idx < 0 || idx >= TIER_ORDER.length - 1) return null;
  return tierLabel(TIER_ORDER[idx + 1]);
};

// points needed to *reach* `level`. Inverse of compute_level_tier in SQL.
//   level = floor(sqrt(xp/50)) + 1   ⇒   xp_for(level) = (level-1)^2 * 50
export const xpForLevel = (level: number): number => Math.max(0, (level - 1) ** 2 * 50);

// Level at which each tier begins. Mirrors compute_level_tier in SQL —
// keep these two in sync if you ever rebalance the curve.
const TIER_MIN_LEVEL: Record<string, number> = {
  script_kiddie: 1,
  apprentice: 10,
  operator: 20,
  hacker: 30,
  wizard: 50,
  architect: 80,
};

// points threshold the user must reach to enter `tier`.
export const xpForTier = (tier: string): number =>
  xpForLevel(TIER_MIN_LEVEL[tier] ?? 1);

export const xpProgressInLevel = (totalXp: number, level: number) => {
  const floor = xpForLevel(level);
  const ceiling = xpForLevel(level + 1);
  const span = Math.max(1, ceiling - floor);
  const into = Math.max(0, Math.min(span, totalXp - floor));
  return { into, span, pct: Math.round((into / span) * 100), floor, ceiling };
};
